import type {
  ApiReviewReason,
  CourseStatus,
  EnrollmentStatus,
  OccupancySource,
  RejectReasonType,
  RequestStatus,
  ReservationStatus,
} from '../types/api';

export const STATUS_LABEL = {
  pending: '待审核',
  approved: '已通过',
  rejectedConflict: '已驳回-时段冲突',
  rejectedSafety: '已驳回-安全原因',
  rejectedFeasibility: '已驳回-可行性',
  cancelled: '已取消',
  expired: '已过期',
  enrolled: '已报名',
  waitlisted: '已候补',
  done: '已完成',
} as const;

export function mapUiReason(reason: RejectReasonType): ApiReviewReason {
  if (reason === 'time_conflict') return 'TIME_CONFLICT';
  if (reason === 'safety') return 'SAFETY_REJECT';
  return 'RESOURCE_UNAVAILABLE';
}

/** 六档驳回理由的中文标签，与后端 REJECT_REASON_LABELS 保持一致。 */
export const REJECT_REASON_LABEL: Record<RejectReasonType, string> = {
  safety: '安全风险',
  materials: '材料试剂不足',
  equipment: '仪器设备不可用',
  time_conflict: '时段冲突',
  procedure: '实验方案不完整',
  other: '其他',
};

export function rejectReasonLabel(code?: string | null): string {
  if (!code) return '未填写原因';
  return REJECT_REASON_LABEL[code as RejectReasonType] ?? code;
}

export function rejectedLabel(reasonType?: string | null): string {
  const v = (reasonType ?? '').toLowerCase();
  if (reasonType === 'TIME_CONFLICT' || v === 'time_conflict' || v.includes('conflict')) {
    return STATUS_LABEL.rejectedConflict;
  }
  if (reasonType === 'SAFETY_REJECT' || v === 'safety' || v.includes('safety')) {
    return STATUS_LABEL.rejectedSafety;
  }
  if (
    reasonType === 'RESOURCE_UNAVAILABLE' ||
    v === 'feasibility' ||
    v.includes('feasib') ||
    v.includes('resource')
  ) {
    return STATUS_LABEL.rejectedFeasibility;
  }
  return '已驳回';
}

export function reservationLabel(
  status: ReservationStatus | RequestStatus | string,
  reasonType?: string | null,
): string {
  if (status === 'pending') return STATUS_LABEL.pending;
  if (status === 'approved') return STATUS_LABEL.approved;
  if (status === 'cancelled' || status === 'withdrawn') return STATUS_LABEL.cancelled;
  if (status === 'expired') return STATUS_LABEL.expired;
  if (status === 'completed') return STATUS_LABEL.done;
  if (status === 'rejected') return rejectedLabel(reasonType);
  return String(status);
}

export type Tone =
  | 'pending'
  | 'approved'
  | 'conflict'
  | 'rejected'
  | 'cancelled'
  | 'expired'
  | 'done'
  | 'waitlisted';

export function reservationTone(status: string, reasonType?: string | null): Tone {
  if (status === 'pending') return 'pending';
  if (status === 'approved' || status === 'enrolled') return 'approved';
  if (status === 'waitlisted') return 'waitlisted';
  if (status === 'cancelled' || status === 'withdrawn') return 'cancelled';
  if (status === 'expired') return 'expired';
  if (status === 'completed') return 'done';
  if (status === 'rejected') {
    return rejectedLabel(reasonType) === STATUS_LABEL.rejectedConflict ? 'conflict' : 'rejected';
  }
  return 'cancelled';
}

export function enrollmentLabel(status: EnrollmentStatus): string {
  if (status === 'enrolled') return STATUS_LABEL.enrolled;
  if (status === 'waitlisted') return STATUS_LABEL.waitlisted;
  return STATUS_LABEL.cancelled;
}

export function courseStatusLabel(status: CourseStatus): string {
  const map: Record<CourseStatus, string> = {
    draft: '草稿',
    published: '已发布',
    signup_closed: '报名已截止',
    completed: '已完成',
    cancelled: '已取消',
  };
  return map[status];
}

export function sourceLabel(source: OccupancySource | string): string {
  if (source === 'teacher') return '教师预约';
  if (source === 'experiment_request') return '学生自主实验';
  if (source === 'open_course') return '开放课';
  if (source === 'legacy_migration') return '迁移占用';
  return source;
}

export function openStatusLabel(v: string): string {
  if (v === 'open') return '开放';
  if (v === 'maintenance') return '维护';
  return '关闭';
}

export function verificationLabel(v: string): string {
  return v === 'verified' ? '已核验' : '待核验';
}

export function isBookable(lab: { open_status: string; verification_status: string }): boolean {
  return lab.open_status === 'open' && lab.verification_status === 'verified';
}

export function consentLabel(raw?: string | null): string {
  if (raw === 'verified') return '授权已核验';
  if (raw === 'not_applicable') return '已脱敏';
  if (raw === 'denied' || raw === 'rejected') return '不予公开';
  return '授权待核验';
}

export const MISSING_REASON = '原因缺失，请联系实验员补充';
export const CANCEL_RULE = '开课前24小时（含恰好24小时）可取消；之后联系实验员。';

export function canSelfCancel(startsAt: string, now = new Date()): boolean {
  const start = new Date(startsAt).getTime();
  if (Number.isNaN(start)) return false;
  return now.getTime() + 24 * 60 * 60 * 1000 <= start;
}

export function remainingSeats(course: { capacity: number; enrolled_count: number; remaining?: number }): number {
  if (typeof course.remaining === 'number') return course.remaining;
  return Math.max(0, course.capacity - course.enrolled_count);
}
